import { Controller } from "@hotwired/stimulus";
import L from "leaflet";
import * as Lm from "leaflet.markercluster";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { PieChart } from "chartist";

export class GeoJsonController extends Controller {
  static targets = ["map", "card", "clusterTemplate", "cluster"];
  static classes = ["clusterIcon"];
  static values = {
    source: String,
    acontecimientoLayout: {
      type: String,
      default: "#5080de",
    },
    intervencionLayout: {
      type: String,
      default: "#ed7126",
    },
    nominatimLayout: {
      type: String,
      default: "#874594",
    },
    clusterIconSize: {
      type: Number,
      default: 52,
    },
    layouts: {
      type: Array,
      default: ["acontecimiento", "intervencion", "nominatim"],
    },
  };

  async mapTargetConnected(mapTarget) {
    this.map;

    try {
      const featureCollection = await (await fetch(this.sourceValue)).json();

      this.dispatch("populate", { detail: { featureCollection } });
    } catch (e) {
      console.error(e);
    }
  }

  populateMap({ detail: { featureCollection } }) {
    if (featureCollection?.features?.length === 0) {
      console.log(featureCollection);
      return;
    }

    this.map.eachLayer((x) => {
      if (!(x instanceof L.TileLayer)) this.map.removeLayer(x);
    });

    this.closeCard();

    const geoJson = L.geoJSON(featureCollection, {
      pointToLayer: (geoJsonPoint, latLng) => {
        return L.marker(latLng, {
          icon: L.icon({
            iconUrl: `assets/map/${geoJsonPoint.properties.layout}.svg`,
            iconSize: [29, 29],
            iconAnchor: [18, 18],
          }),
        });
      },
    }).on("click", async (event) => {
      const { feature_collection, card, layout } =
        event.layer.feature.properties;

      if (layout === "nominatim") return;

      this.closeCard();

      const response = await (await fetch(feature_collection)).json();

      const geoJsonFeature = L.geoJSON(response, {
        style: this.styleGeoJson.bind(this),
        pointToLayer: this.pointToLayer.bind(this),
      });

      geoJsonFeature.addTo(this.map);

      this.currentFeature = geoJsonFeature;

      if (card) {
        this.cardTarget.innerHTML = await (await fetch(card)).text();
      }
    });

    const markerClusterGroup = L.markerClusterGroup({
      iconCreateFunction: this.createCluster.bind(this),
      showCoverageOnHover: false,
      disableClusteringAtZoom: 13,
      spiderfyOnMaxZoom: false,
    });
    markerClusterGroup.addLayer(geoJson);
    this.map.addLayer(markerClusterGroup);

    this.map.fitBounds(geoJson.getBounds());
  }

  mapTargetDisconnected(mapTarget) {
    this.map.remove();
  }

  get map() {
    if (!this._map) {
      this._map = L.map(this.mapTarget, { attributionControl: false, zoomControl: false }).setView(
        [0, 0],
        5
      );

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
      }).addTo(this._map);

      L.control.zoom({
    position: "topright"
      }).addTo(this.map)
    }
    return this._map;
  }

  resize(event) {
    this.map.invalidateSize();
  }

  styleGeoJson(geoJsonFeature) {
    return { color: this[`${geoJsonFeature.properties.layout}LayoutValue`] };
  }

  pointToLayer(geoJsonPoint, latLng) {
    return L.circleMarker(latLng, {
      radius: 5,
      fill: true,
      fillOpacity: 1,
      color: this[`${geoJsonPoint.properties.layout}LayoutValue`],
    });
  }

  closeCard(event = undefined) {
    this.currentFeature?.remove();
    this.cardTarget.innerHTML = "";
  }

  createCluster(cluster) {
    const groupedByLayout = Map.groupBy(
      cluster.getAllChildMarkers(),
      (marker) => marker.feature.properties.layout
    );

    const clusterMarker =
      this.clusterTemplateTarget.content.firstElementChild.cloneNode(true);

    for (const [layout, markers] of groupedByLayout) {
      clusterMarker.dataset[layout] = markers.length;
    }

    clusterMarker.dataset.total = cluster.getChildCount();

    return L.divIcon({
      html: clusterMarker,
      iconSize: [this.clusterIconSizeValue, this.clusterIconSizeValue],
      className: this.clusterIconClass,
    });
  }

  clusterTargetConnected(clusterTarget) {
    const series = [];

    for (const layout of this.layoutsValue) {
      series.push(parseInt(clusterTarget.dataset[layout] || "0"));
    }

    new PieChart(
      clusterTarget.querySelector("[data-cluster-chart]"),
      { series },
      {
        donut: true,
        showLabel: false,
        donutWidth: 6,
        total: parseInt(clusterTarget.dataset.total),
        chartPadding: 0,
      }
    );

    clusterTarget.querySelector("[data-cluster-total]").innerText =
      clusterTarget.dataset.total;
  }
}
